import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, eur, stationLabel, formatApiError } from "@/lib/api";
import { readCart, cartTotals, clearCart } from "@/lib/cart";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import { Users, Sparkles, Award, Heart } from "lucide-react";
import { enqueueOrder } from "@/lib/orderQueue";
import haptic from "@/lib/haptic";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^\+?[0-9 ]{7,20}$/;

export default function Checkout() {
  const [cart, setCart] = useState(readCart());
  const [settings, setSettings] = useState(null);
  const [menu, setMenu] = useState(null);
  // Privacy pre-selezionata, marketing pre-selezionata (default per nuovi clienti)
  const [form, setForm] = useState({
    phone: "",
    name: "",
    surname: "",
    email: "",
    privacy: true,
    marketing: true,
  });
  const [payment, setPayment] = useState("on_delivery");
  const [people, setPeople] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  // Sprint 0.1 — id dell'entry in coda se stiamo trasmettendo in background
  const [pendingQueueId, setPendingQueueId] = useState(null);
  // Fase 18 — profilo cliente ricorrente
  const [profile, setProfile] = useState(null);
  // true dopo un lookup positivo, false appena il cliente modifica un campo
  const [prefilled, setPrefilled] = useState(false);
  const submitBtnRef = useRef(null);
  const submittedRef = useRef(false);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/public/settings").then((r) => setSettings(r.data));
    api.get("/public/menu").then((r) => setMenu(r.data));
    const h = () => setCart(readCart());
    window.addEventListener("sb-cart-change", h);
    return () => window.removeEventListener("sb-cart-change", h);
  }, []);

  useEffect(() => {
    if (submittedRef.current) return; // dopo l'invio non deviare da /status/{id}
    if (!cart.station) nav("/");
    if (cart.items.length === 0) nav("/menu");
  }, [cart, nav]);

  const categoryByProductId = useMemo(() => {
    const map = {};
    (menu?.products || []).forEach((p) => { map[p.id] = p.category_id; });
    return map;
  }, [menu]);
  const copertoCategoryIds = useMemo(
    () => (menu?.categories || []).filter((c) => c.requires_coperto).map((c) => c.id),
    [menu],
  );

  const totals = cartTotals(cart, {
    serviceChargeCents: settings?.service_charge_cents || 0,
    copertoCents: settings?.coperto_cents || 0,
    peopleCount: people,
    copertoCategoryIds,
    categoryByProductId,
  });

  const idemKey = useMemo(() => crypto.randomUUID(), []);

  const validPhone = PHONE_RE.test(form.phone.trim());
  const validName = form.name.trim().length >= 2;
  const validSurname = form.surname.trim().length >= 2;
  const validEmail = EMAIL_RE.test(form.email.trim());
  const validPeople = !totals.needsCoperto || (people >= 1 && people <= 100);
  const canSubmit =
    validPhone && validName && validSurname && validEmail &&
    form.privacy && validPeople && !submitting;

  // Fase 18 — Bentornato: se il numero è già nel CRM, precompila e mostra il banner.
  useEffect(() => {
    if (!validPhone) { setProfile(null); setPrefilled(false); return; }
    const phone = form.phone.trim();
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const r = await api.get(`/public/customer/lookup?phone=${encodeURIComponent(phone)}`);
        if (cancelled) return;
        setProfile(r.data);
        if (r.data?.found) {
          // Precompila SOLO i campi vuoti — non sovrascrivere ciò che il cliente sta scrivendo.
          // MAPPING ESPLICITO (2026-02-19): customer.name → Nome, customer.surname → Cognome.
          // Non facciamo NESSUN parsing/split/concatenazione dei campi. Se il record CRM
          // è legacy (name contiene più parole, surname null), il valore intero finisce nel
          // campo Nome e sarà il cliente a correggere manualmente — poi il submit salva
          // i due campi separati (retro-compat data).
          setForm((f) => {
            const next = { ...f };
            let touched = false;
            if (!f.name.trim() && r.data.name) { next.name = r.data.name; touched = true; }
            if (!f.surname.trim() && r.data.surname) { next.surname = r.data.surname; touched = true; }
            if (!f.email.trim() && r.data.email) { next.email = r.data.email; touched = true; }
            // Marketing: rispecchia il valore CRM SOLO se il cliente ha una preferenza
            // esplicitamente registrata (marketing_consent_at valorizzato). Altrimenti
            // resta il default (pre-selezionato) — così un record legacy che non è mai
            // stato interrogato non parte a checkbox off.
            if (r.data.marketing_consent_at && typeof r.data.marketing_consent === "boolean") {
              next.marketing = r.data.marketing_consent;
              touched = true;
            }
            if (touched) return next;
            return f;
          });
          setPrefilled(true);
        } else {
          setPrefilled(false);
        }
      } catch { if (!cancelled) { setProfile(null); setPrefilled(false); } }
    }, 400);
    return () => { cancelled = true; clearTimeout(t); };
  }, [form.phone, validPhone]);

  // Cliente ricorrente riconosciuto: sposta il focus sul pulsante "Invia ordine"
  // se tutti i dati sono già validi. Se il cliente modifica un campo, `prefilled`
  // viene rimesso a false e il focus non viene più forzato.
  useEffect(() => {
    if (prefilled && canSubmit && submitBtnRef.current) {
      try { submitBtnRef.current.focus({ preventScroll: true }); } catch { /* noop */ }
    }
  }, [prefilled, canSubmit]);

  // Helper per aggiornare un campo e resettare lo stato "prefilled"
  // (il cliente ha modificato manualmente → il focus automatico non deve più scattare)
  const update = (patch, userEdit = true) => {
    setForm((f) => ({ ...f, ...patch }));
    if (userEdit) setPrefilled(false);
  };

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    const payload = {
      station_token: cart.station.token,
      items: cart.items.map((i) => ({
        product_id: i.product_id, quantity: i.quantity,
        modifiers: i.modifiers, note: i.note,
      })),
      customer: {
        name: form.name.trim(),
        surname: form.surname.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        privacy_consent: form.privacy,
        marketing_consent: form.marketing,
      },
      payment_method: payment,
      people_count: totals.needsCoperto ? people : null,
      idempotency_key: idemKey,
    };
    try {
      const { data } = await api.post("/public/orders", payload);
      submittedRef.current = true;
      clearCart();
      haptic.confirm();
      nav(`/status/${data.id}`);
    } catch (e) {
      // Sprint 0.1 — se è un errore di rete, mettiamo in coda locale invece di
      // mostrare un errore. Il replay avverrà appena la rete torna disponibile.
      const isNetwork = !e?.response;
      if (isNetwork) {
        const entry = enqueueOrder(payload);
        setPendingQueueId(entry.id);
        haptic.tap();
        // NON resettiamo `submitting`: l'overlay resta attivo finché la coda smaltisce.
        return;
      }
      toast.error(formatApiError(e));
      setSubmitting(false);
    }
  };

  // Sprint 0.1 — listener eventi coda: successo → nav a status; rifiuto → toast + reset.
  useEffect(() => {
    if (!pendingQueueId) return;
    const onFlushed = (e) => {
      if (e.detail?.entryId !== pendingQueueId) return;
      if (!e.detail?.orderId) return;
      submittedRef.current = true;
      clearCart();
      haptic.confirm();
      nav(`/status/${e.detail.orderId}`);
    };
    const onRejected = (e) => {
      if (e.detail?.entryId !== pendingQueueId) return;
      setPendingQueueId(null);
      setSubmitting(false);
      toast.error(e.detail?.error || "Ordine rifiutato");
    };
    window.addEventListener("sb-order-queue-flushed", onFlushed);
    window.addEventListener("sb-order-queue-rejected", onRejected);
    return () => {
      window.removeEventListener("sb-order-queue-flushed", onFlushed);
      window.removeEventListener("sb-order-queue-rejected", onRejected);
    };
  }, [pendingQueueId, nav]);

  const onFormKeyDown = (e) => {
    // Cliente ricorrente: premendo INVIO da qualsiasi campo → invia direttamente
    if (e.key === "Enter" && canSubmit) {
      e.preventDefault();
      submit();
    }
  };

  return (
    <div className="min-h-screen px-5 pt-8 pb-32">
      <Link to="/menu" className="eyebrow text-bronze-600" data-testid="back-to-menu">← Torna al menu</Link>
      <h1 className="mt-3 text-4xl">Conferma ordine</h1>
      <div className="mt-1 text-ink-500">Consegna a <span className="text-ink-900 font-medium">{stationLabel(cart.station)}</span></div>

      <section className="card-elevated mt-6 p-5">
        <div className="eyebrow mb-3">I tuoi prodotti</div>
        <div className="space-y-2">
          {cart.items.map((i) => {
            const unit = i.unit_price_cents + i.modifiers.reduce((a, m) => a + (m.price_delta_cents || 0), 0);
            return (
              <div key={i.uid} className="flex justify-between text-sm">
                <div>
                  <div>{i.quantity}× {i.product_name}</div>
                  {i.modifiers.map((m, idx) => <div key={idx} className="text-xs text-ink-500">· {m.option_name}</div>)}
                </div>
                <div className="font-serif">{eur(unit * i.quantity)}</div>
              </div>
            );
          })}
        </div>
        <div className="border-t border-sand-300 mt-3 pt-3 space-y-1 text-sm">
          <div className="flex justify-between"><span>Subtotale</span><span data-testid="checkout-subtotal">{eur(totals.subtotal)}</span></div>
          {totals.service > 0 && <div className="flex justify-between"><span>Service consegna</span><span data-testid="checkout-service">{eur(totals.service)}</span></div>}
          {totals.needsCoperto && <div className="flex justify-between"><span>Coperto {people}× {eur(totals.copertoUnit)}</span><span data-testid="checkout-coperto">{eur(totals.coperto)}</span></div>}
          <div className="flex justify-between font-serif text-xl pt-1"><span>Totale</span><span data-testid="checkout-total">{eur(totals.total)}</span></div>
        </div>
      </section>

      {totals.needsCoperto && (
        <section className="card-elevated mt-4 p-5" data-testid="coperto-section">
          <div className="eyebrow mb-2 flex items-center gap-1"><Users className="w-3 h-3" /> Coperto obbligatorio</div>
          <p className="text-sm text-ink-500 mb-3">Il tuo ordine contiene articoli che richiedono il coperto ({eur(totals.copertoUnit)} per persona).</p>
          <Label htmlFor="people">Quante persone mangiano?</Label>
          <Input id="people" type="number" min={1} max={100} value={people}
                 onChange={(e) => setPeople(Math.max(1, parseInt(e.target.value) || 1))}
                 data-testid="people-count-input" className="mt-1" />
          {!validPeople && <p className="text-xs text-red-700 mt-1">Numero non valido (minimo 1, massimo 100).</p>}
        </section>
      )}

      <section className="card-elevated mt-4 p-5 space-y-4" onKeyDown={onFormKeyDown}>
        <div className="eyebrow">I tuoi dati</div>

        {/* 1) Telefono — primo campo, driver dell'auto-compilazione */}
        <div>
          <Label htmlFor="phone">Numero di telefono</Label>
          <Input
            id="phone"
            data-testid="customer-phone"
            value={form.phone}
            onChange={(e) => update({ phone: e.target.value }, false)}
            placeholder="+39 333 1234567"
            inputMode="tel"
            autoFocus
          />
          {form.phone && !validPhone && <p className="text-xs text-red-700 mt-1">Numero non valido</p>}
          {profile?.found && (
            <div className="mt-2 rounded-xl border border-emerald-300 bg-emerald-50 px-3 py-2"
                 data-testid="bentornato-banner">
              <div className="text-sm font-semibold text-emerald-900 flex items-center gap-1">
                <Sparkles className="w-4 h-4" /> Bentornato{profile.name ? `, ${profile.name}` : ""}!
              </div>
              <div className="text-xs text-emerald-800 mt-0.5">
                {profile.orders_count} visita{profile.orders_count === 1 ? "" : "e"} · abbiamo memorizzato le tue preferenze
              </div>
              <div className="mt-1 flex items-center flex-wrap gap-1.5">
                {profile.loyalty?.enabled && (
                  <span className="text-[10px] uppercase tracking-wider rounded-full border border-bronze-300 bg-bronze-50 text-bronze-800 px-2 py-0.5 flex items-center gap-0.5"
                        data-testid="bentornato-points">
                    <Award className="w-3 h-3" /> {profile.loyalty.points} punti
                  </span>
                )}
                {(profile.badges || []).slice(0, 3).map((b) => (
                  <span key={b} className="text-[10px] uppercase tracking-wider rounded-full border border-emerald-300 bg-white text-emerald-800 px-2 py-0.5"
                        data-testid={`bentornato-badge-${b}`}>
                    {b}
                  </span>
                ))}
              </div>
              {profile.favorites?.length > 0 && (
                <div className="mt-1.5 text-xs text-emerald-800 flex items-center gap-1">
                  <Heart className="w-3 h-3" /> Preferiti: {profile.favorites.slice(0, 3).map((f) => f.product_name).join(" · ")}
                </div>
              )}
            </div>
          )}
        </div>

        {/* 2) Nome */}
        <div>
          <Label htmlFor="name">Nome</Label>
          <Input id="name" data-testid="customer-name" value={form.name}
                 onChange={(e) => update({ name: e.target.value })} placeholder="Mario" autoComplete="given-name" />
        </div>

        {/* 3) Cognome */}
        <div>
          <Label htmlFor="surname">Cognome</Label>
          <Input id="surname" data-testid="customer-surname" value={form.surname}
                 onChange={(e) => update({ surname: e.target.value })} placeholder="Rossi" autoComplete="family-name" />
        </div>

        {/* 4) Email (obbligatoria) */}
        <div>
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" data-testid="customer-email" value={form.email}
                 onChange={(e) => update({ email: e.target.value })} placeholder="mario@esempio.it" autoComplete="email" />
          {form.email && !validEmail && <p className="text-xs text-red-700 mt-1">Email non valida</p>}
        </div>

        <div className="flex items-start gap-2">
          <Checkbox id="privacy" data-testid="privacy-check" checked={form.privacy}
                    onCheckedChange={(v) => update({ privacy: !!v })} />
          <Label htmlFor="privacy" className="text-sm leading-relaxed">
            Ho letto e accetto l'informativa sulla privacy (obbligatorio) — trattamento per gestione ordine.
          </Label>
        </div>
        <div className="flex items-start gap-2">
          <Checkbox id="marketing" data-testid="marketing-check" checked={form.marketing}
                    onCheckedChange={(v) => update({ marketing: !!v })} />
          <Label htmlFor="marketing" className="text-sm leading-relaxed">
            Voglio ricevere comunicazioni promozionali di Sarabi Beach (facoltativo).
          </Label>
        </div>
      </section>

      <section className="card-elevated mt-4 p-5">
        <div className="eyebrow mb-3">Metodo di pagamento</div>
        <RadioGroup value={payment} onValueChange={setPayment}>
          <div className="flex items-center gap-2 py-2">
            <RadioGroupItem value="on_delivery" id="p1" data-testid="pay-on-delivery" />
            <Label htmlFor="p1">Pagamento alla consegna</Label>
          </div>
          {settings?.station_tab_enabled && (
            <div className="flex items-center gap-2 py-2">
              <RadioGroupItem value="station_tab" id="p2" data-testid="pay-station-tab" />
              <Label htmlFor="p2">Addebita al conto della postazione</Label>
            </div>
          )}
          {settings?.at_cashier_enabled && (
            <div className="flex items-center gap-2 py-2">
              <RadioGroupItem value="at_cashier" id="p3" data-testid="pay-at-cashier" />
              <Label htmlFor="p3">Pagamento alla cassa</Label>
            </div>
          )}
        </RadioGroup>
        <div className="mt-2 text-[11px] text-ink-500">SumUp e PayPal non ancora configurati — pagamento online sarà disponibile in una fase successiva.</div>
      </section>

      <div className="fixed bottom-0 left-0 right-0 glass-cart px-5 py-4">
        <Button ref={submitBtnRef} data-testid="submit-order-btn" disabled={!canSubmit} onClick={submit} className="w-full rounded-full h-12 text-base">
          {submitting ? "Invio…" : `Invia ordine · ${eur(totals.total)}`}
        </Button>
      </div>

      {/* Sprint 0.1 — overlay neutro mentre la coda offline trasmette l'ordine */}
      {pendingQueueId && (
        <div
          className="fixed inset-0 z-40 bg-sand-50/95 backdrop-blur-sm flex items-center justify-center px-6"
          data-testid="offline-transmit-overlay"
          role="status"
          aria-live="polite"
        >
          <div className="max-w-sm text-center">
            <div className="mx-auto w-16 h-16 rounded-full border-2 border-bronze-500/40 border-t-bronze-600 animate-spin"
                 style={{ animationDuration: "1.4s" }} />
            <div className="mt-6 font-serif text-2xl text-ink-900">
              Stiamo trasmettendo il tuo ordine…
            </div>
            <div className="mt-2 text-sm text-ink-700">
              Non chiudere questa pagina.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
